import { useState, useEffect, useCallback, useMemo } from "react";
import Loading from "../elements/Loading";
import InlineNotification from "../elements/InlineNotification";
import getAPIClient from "../hooks/apiClient";
import s from '../styles/pages/VacancySettingsPage.module.scss';
import { FaPlus, FaTrash } from "react-icons/fa";

export default function VacancySettingsPage({ BX24, vacancyItemId }) {
    const api = useMemo(() => getAPIClient(BX24), []);
    const [isLoading, setIsLoading] = useState(true);
    const [notificationText, setNotificationText] = useState('');
    const [notificationType, setNotificationType] = useState('error');
    const [crmItemId, setCrmItemId] = useState(null);
    const [vacancyData, setVacancyData] = useState({
        hh_import_enabled: false,
        hh_import_state_ids: [],
        name: '',
        description: '',
        competencies: [''],
        requirements: ['']
    });
    const [hhStagesLoading, setHhStagesLoading] = useState(false);
    const [hhStageOptions, setHhStageOptions] = useState([]);

    const handleResponseError = useCallback((defaultText, e) => {
        console.error('Error:', e);
        setNotificationType('error');
        if (e?.response?.data?.detail) {
            setNotificationText(`${e.response.data.detail}`);
        } else {
            setNotificationText(defaultText);
        }
    }, []);

    const fetchHhStageOptions = useCallback(async () => {
        if (!crmItemId) return [];
        setHhStagesLoading(true);
        try {
            const response = await api.get(`/analysis_agent/vacancies/hh/states?crm_item_id=${crmItemId}`);
            const states = Array.isArray(response.data) ? response.data : [];
            setHhStageOptions(states);
            return states;
        } catch (e) {
            handleResponseError('Ошибка загрузки этапов HH', e);
            setHhStageOptions([]);
            return [];
        } finally {
            setHhStagesLoading(false);
        }
    }, [api, crmItemId, handleResponseError]);

    // Загрузка данных вакансии
    const fetchVacancyData = useCallback(async () => {
        if (!crmItemId) return;

        setIsLoading(true);
        try {
            const response = await api.get(`/analysis_agent/vacancies?crm_item_id=${crmItemId}`);
            if (response.data) {
                setVacancyData({
                    hh_import_enabled: Boolean(response.data.hh_import_enabled),
                    hh_import_state_ids: Array.isArray(response.data.hh_import_state_ids)
                        ? response.data.hh_import_state_ids
                        : [],
                    name: response.data.name || '',
                    description: response.data.description || '',
                    competencies: response.data.competencies?.length
                        ? response.data.competencies
                        : [''],
                    requirements: response.data.requirements?.length
                        ? response.data.requirements
                        : ['']
                });
                if (response.data.hh_import_enabled) {
                    await fetchHhStageOptions();
                } else {
                    setHhStageOptions([]);
                }
            }
        } catch (e) {
            handleResponseError('Ошибка загрузки данных вакансии', e);
        } finally {
            setIsLoading(false);
        }
    }, [api, crmItemId, fetchHhStageOptions, handleResponseError]);

    // Сохранение данных
    const saveVacancySettings = useCallback(async () => {
        setIsLoading(true);
        try {
            // Фильтруем пустые значения перед отправкой
            const dataToSend = {
                hh_import_enabled: Boolean(vacancyData.hh_import_enabled),
                hh_import_state_ids: vacancyData.hh_import_enabled
                    ? (vacancyData.hh_import_state_ids || [])
                    : [],
                name: vacancyData.name,
                description: vacancyData.description,
                competencies: vacancyData.competencies.filter(c => c.trim() !== ''),
                requirements: vacancyData.requirements.filter(r => r.trim() !== '')
            };

            await api.patch(`/analysis_agent/vacancies?crm_item_id=${crmItemId}`, dataToSend);

            setNotificationType('success');
            setNotificationText('Настройки успешно сохранены');
        } catch (e) {
            handleResponseError('Ошибка сохранения настроек', e);
        } finally {
            setIsLoading(false);
        }
    }, [api, crmItemId, handleResponseError, vacancyData]);

    // Инициализация
    useEffect(() => {
        const init = async () => {
            if (vacancyItemId) {
                setCrmItemId(vacancyItemId)
                return
            }
            try {
                const placement = await BX24.placement.info();
                const itemId = placement?.options?.ID || placement?.options?.ENTITY_ID;
                if (itemId) {
                    setCrmItemId(itemId);
                } else {
                    setNotificationType('error');
                    setNotificationText('Не удалось получить ID вакансии');
                    setIsLoading(false);
                }
            } catch (e) {
                handleResponseError('Ошибка инициализации', e);
                setIsLoading(false);
            }
        };
        init();
    }, [BX24, handleResponseError, vacancyItemId]);

    // При изменении ID вакансии загружаем данные
    useEffect(() => {
        if (crmItemId) {
            fetchVacancyData();
        }
    }, [crmItemId, fetchVacancyData]);

    // Обработчики изменений полей
    const handleNameChange = (e) => {
        setVacancyData(prev => ({ ...prev, name: e.target.value }));
    };

    const handleDescriptionChange = (e) => {
        setVacancyData(prev => ({ ...prev, description: e.target.value }));
    };

    const handleHhSyncToggle = async (e) => {
        const enabled = e.target.checked;
        setVacancyData(prev => ({
            ...prev,
            hh_import_enabled: enabled,
            hh_import_state_ids: enabled ? prev.hh_import_state_ids : []
        }));
        if (enabled) {
            await fetchHhStageOptions();
        } else {
            setHhStageOptions([]);
        }
    };

    const handleHhStageSelect = (e) => {
        const stageId = e.target.value;
        setVacancyData(prev => ({
            ...prev,
            hh_import_state_ids: stageId ? [stageId] : []
        }));
    };

    // Работа со списком компетенций
    const handleCompetencyChange = (index, value) => {
        setVacancyData(prev => {
            const newCompetencies = [...prev.competencies];
            newCompetencies[index] = value;
            return { ...prev, competencies: newCompetencies };
        });
    };

    const addCompetency = () => {
        setVacancyData(prev => ({
            ...prev,
            competencies: [...prev.competencies, '']
        }));
    };

    const removeCompetency = (index) => {
        if (vacancyData.competencies.length <= 1) return;

        setVacancyData(prev => {
            const newCompetencies = [...prev.competencies];
            newCompetencies.splice(index, 1);
            return { ...prev, competencies: newCompetencies };
        });
    };

    // Работа со списком ограничений
    const handleRequirementChange = (index, value) => {
        setVacancyData(prev => {
            const newRequirements = [...prev.requirements];
            newRequirements[index] = value;
            return { ...prev, requirements: newRequirements };
        });
    };

    const addRequirement = () => {
        setVacancyData(prev => ({
            ...prev,
            requirements: [...prev.requirements, '']
        }));
    };

    const removeRequirement = (index) => {
        if (vacancyData.requirements.length <= 1) return;

        setVacancyData(prev => {
            const newRequirements = [...prev.requirements];
            newRequirements.splice(index, 1);
            return { ...prev, requirements: newRequirements };
        });
    };
    return (
        <div className={s.container}>
            <div className={s.mainBlock}>
                {notificationText && <InlineNotification
                    type={notificationType}
                    text={notificationText}
                    hideFlag={notificationText.length === 0}
                />}

                {isLoading ? (
                    <Loading isLoading={true} />
                ) : (
                    <div className={s.form}>
                        <div className={s.formGroup}>
                            <label htmlFor="name">Название вакансии</label>
                            <input
                                type="text"
                                id="name"
                                value={vacancyData.name}
                                onChange={handleNameChange}
                                placeholder="Введите название"
                                className={s.input}
                            />
                        </div>

                        <div className={s.formGroup}>
                            <label htmlFor="description">Описание вакансии</label>
                            <textarea
                                id="description"
                                value={vacancyData.description}
                                onChange={handleDescriptionChange}
                                placeholder="Введите описание вакансии"
                                rows="4"
                                className={s.textarea}
                            />
                        </div>

                        <div className={s.formGroup}>
                            <div className={s.hhSyncRow}>
                                <label className={s.checkboxLabel}>
                                    <input
                                        type="checkbox"
                                        checked={Boolean(vacancyData.hh_import_enabled)}
                                        onChange={handleHhSyncToggle}
                                    />
                                    <span>Синхронизация по этапу HH</span>
                                </label>
                            </div>
                            {vacancyData.hh_import_enabled && (
                                <div className={s.hhStageBlock}>
                                    <div className={s.hhStageHeader}>
                                        <label htmlFor="hh-stage">Этап HH для импорта резюме</label>
                                        <button
                                            type="button"
                                            className={s.secondaryButton}
                                            onClick={fetchHhStageOptions}
                                            disabled={hhStagesLoading}
                                        >
                                            {hhStagesLoading ? 'Обновляем...' : 'Обновить этапы'}
                                        </button>
                                    </div>
                                    <select
                                        id="hh-stage"
                                        className={s.select}
                                        value={vacancyData.hh_import_state_ids?.[0] || ''}
                                        onChange={handleHhStageSelect}
                                        disabled={hhStagesLoading}
                                    >
                                        <option value="">Выберите этап</option>
                                        {hhStageOptions.map(stage => (
                                            <option key={stage.id} value={stage.id}>
                                                {stage.name}
                                            </option>
                                        ))}
                                    </select>
                                    <p className={s.hintText}>
                                        Если включено, импорт будет только при переходе отклика в выбранный этап HH.
                                    </p>
                                </div>
                            )}
                        </div>

                        <div className={s.formGroup}>
                            <div className={s.listHeader}>
                                <label>Компетенции (тут нужно ввести список необходимых hard-skills)</label>
                                <button
                                    type="button"
                                    className={s.addButton}
                                    onClick={addCompetency}
                                >
                                    <FaPlus /> Добавить
                                </button>
                            </div>

                            <div className={s.listItems}>
                                {vacancyData.competencies.map((competency, index) => (
                                    <div key={index} className={s.listItem}>
                                        <input
                                            type="text"
                                            value={competency}
                                            onChange={(e) => handleCompetencyChange(index, e.target.value)}
                                            placeholder={`Компетенция ${index + 1}`}
                                            className={s.input}
                                        />
                                        <button
                                            type="button"
                                            className={s.removeButton}
                                            onClick={() => removeCompetency(index)}
                                            disabled={vacancyData.competencies.length <= 1}
                                        >
                                            <FaTrash />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className={s.formGroup}>
                            <div className={s.listHeader}>
                                <label>Ограничения (пример: проживает в Екатеринбурге)</label>
                                <button
                                    type="button"
                                    className={s.addButton}
                                    onClick={addRequirement}
                                >
                                    <FaPlus /> Добавить
                                </button>
                            </div>

                            <div className={s.listItems}>
                                {vacancyData.requirements.map((requirement, index) => (
                                    <div key={index} className={s.listItem}>
                                        <input
                                            type="text"
                                            value={requirement}
                                            onChange={(e) => handleRequirementChange(index, e.target.value)}
                                            placeholder={`Ограничение ${index + 1}`}
                                            className={s.input}
                                        />
                                        <button
                                            type="button"
                                            className={s.removeButton}
                                            onClick={() => removeRequirement(index)}
                                            disabled={vacancyData.requirements.length <= 1}
                                        >
                                            <FaTrash />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className={s.actions}>
                            <button
                                className={s.primaryButton}
                                onClick={saveVacancySettings}
                                disabled={isLoading}
                            >
                                {isLoading ? 'Сохранение...' : 'Сохранить настройки'}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
